import { Injectable } from "@angular/core";
import { HttpClient, HttpContext, HttpHeaders } from "@angular/common/http";
import { BehaviorSubject, Observable, of } from "rxjs";
import { catchError, filter, map, tap } from "rxjs/operators";
import { v4 as uuidv4 } from "uuid";
import { environment } from "../../../environments/environment";
import { ApiResponse } from "./announcement.models";
import { SKIP_ERROR_ALERT } from "../../shared/interceptors/skip-error-alert.context";

export interface GuideSetting {
  id?: number;
  reference?: string;
  guideKey: string;
  isActive?: boolean;
  /**
   * Legacy spelling. Lombok's isActive()/setActive() made older waba builds publish the flag as
   * "active"; reading only "isActive" made every stored row look disabled here.
   */
  active?: boolean;
}

/** True unless the row explicitly says otherwise, under either wire spelling. */
export function isGuideSettingActive(setting: GuideSetting): boolean {
  return setting.isActive ?? setting.active ?? true;
}

/**
 * Availability of each in-app guide (waba GuideSettingApi / GuideSettingClientApi).
 * A guide with no stored row is enabled, so the frontend catalog can grow without
 * anyone having to seed rows first.
 */
@Injectable({ providedIn: "root" })
export class GuideSettingApiService {
  private readonly adminUrl = `${environment.wabaApiUrl}/v1/api/admin-ui/guide-setting`;
  private readonly clientUrl = `${environment.wabaApiUrl}/v1/api/guide-setting-client`;

  /** Latest known disabled keys; null until the first response lands. */
  private readonly disabledSubject = new BehaviorSubject<string[] | null>(null);
  private loading = false;

  constructor(private http: HttpClient) {}

  private headers(): HttpHeaders {
    return new HttpHeaders({ "x-api-requestId": uuidv4(), "x-api-canal": "web" });
  }

  /** Admin screen: every stored setting. */
  list(): Observable<GuideSetting[]> {
    return this.http
      .get<ApiResponse<GuideSetting[]>>(`${this.adminUrl}/list`)
      .pipe(map((response) => response.body ?? []));
  }

  /**
   * Both spellings go out: a waba build that expects "active" would otherwise ignore "isActive"
   * and fall back to its default of true, silently saving every guide as enabled.
   */
  save(setting: GuideSetting): Observable<ApiResponse<GuideSetting>> {
    const active = isGuideSettingActive(setting);
    const body: GuideSetting = { ...setting, isActive: active, active };
    return this.http
      .put<ApiResponse<GuideSetting>>(`${this.adminUrl}/save`, body, { headers: this.headers() })
      // A change must be visible to the tour service without a page reload.
      .pipe(tap(() => this.refresh()));
  }

  /**
   * Runtime: keys the user must not be shown. Long-lived, so a subscriber taken once (the
   * tour service takes it at construction) also sees every later refresh — dropping a cached
   * one-shot observable here would leave that subscriber stuck on the settings it read first.
   * Fetched at most once per refresh because every page that starts a tour asks for it, and it
   * fails soft — an unreachable endpoint must never block a guide.
   */
  disabledKeys(): Observable<string[]> {
    if (this.disabledSubject.value === null && !this.loading) {
      this.fetchDisabled();
    }
    return this.disabledSubject.pipe(
      filter((keys): keys is string[] => keys !== null),
    );
  }

  /** Re-reads the settings so an admin's toggle reaches the running app immediately. */
  refresh(): void {
    // Nothing has read them yet: the next reader will fetch, so skip the round trip.
    if (this.disabledSubject.value === null && !this.loading) {
      return;
    }
    this.fetchDisabled();
  }

  private fetchDisabled(): void {
    this.loading = true;
    this.http
      .get<ApiResponse<string[]>>(`${this.clientUrl}/disabled`, {
        context: new HttpContext().set(SKIP_ERROR_ALERT, true),
      })
      .pipe(
        map((response) => response.body ?? []),
        catchError(() => of<string[]>([])),
      )
      .subscribe((keys) => {
        this.loading = false;
        this.disabledSubject.next(keys);
      });
  }
}
